import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  BG_ICON_COLOR,
  EVOTRUX_TOAST_BG,
  EVOTRUX_TOAST_BTN_BORDER,
  EVOTRUX_TOAST_BTN_TEXT,
  EVOTRUX_TOAST_HOVER_BG,
  EVOTRUX_TOAST_ICON,
  EVOTRUX_TOAST_SHADOW,
} from "constants/index";
import { useShipmentLiveUpdates } from "hooks";

import type {
  ShipmentUpdateType,
  UseShipmentLiveUpdatesProps,
} from "hooks/useShipmentLiveUpdates";

const UPDATE_TYPE_COPY: Record<ShipmentUpdateType, string> = {
  SHIPMENT_CANCELLED: "Shipment cancelled",
  SHIPMENT_DELIVERED: "Shipment delivered",
  SHIPMENT_STATE_CHANGED: "Shipment status changed",
  SHIPMENT_PICKED_UP: "Shipment picked up",
  DELIVERY_UPDATED: "Delivery updated",
  ROUTE_ETA_UPDATED: "ETA updated",
  QUOTE_UPDATED: "Quote updated",
  QUOTE_RECEIVED: "New quote received",
  SHIPMENT_QA_UPDATED: "Q&A updated",
  QUOTE_QA_UPDATED: "Q&A updated",
  QUOTE_SCORE_UPDATED: "Quote score updated",
};

interface ShipmentLiveUpdateBannerProps {
  pendingUpdateType: ShipmentUpdateType | null;
  onAccept: () => void;
  onDismiss: () => void;
}

function ShipmentLiveUpdateBanner(props: ShipmentLiveUpdateBannerProps) {
  const { pendingUpdateType, onAccept, onDismiss } = props;

  return (
    <Snackbar
      open={pendingUpdateType !== null}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Paper
        role="status"
        aria-live="polite"
        elevation={0}
        sx={{
          width: { xs: "calc(100vw - 32px)", sm: 380 },
          maxWidth: "100%",
          backgroundColor: EVOTRUX_TOAST_BG,
          borderRadius: "8px",
          color: BG_ICON_COLOR,
          boxShadow: EVOTRUX_TOAST_SHADOW,
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            gap: 1.5,
            minHeight: 56,
            px: 2,
            py: 1,
          }}
        >
          <NotificationsIcon
            aria-hidden
            sx={{ color: EVOTRUX_TOAST_ICON, flexShrink: 0, fontSize: 22 }}
          />
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {pendingUpdateType !== null
              ? (UPDATE_TYPE_COPY[pendingUpdateType] ?? "Shipment updated")
              : ""}
          </Typography>
          <Tooltip title="Reload updated data without leaving the page">
            <Button
              variant="outlined"
              size="small"
              onClick={onAccept}
              sx={{
                borderColor: EVOTRUX_TOAST_BTN_BORDER,
                borderRadius: "4px",
                color: EVOTRUX_TOAST_BTN_TEXT,
                fontSize: "12px",
                fontWeight: 500,
                minHeight: 32,
                minWidth: 0,
                ml: "auto",
                px: 1,
                "&:hover": {
                  borderColor: EVOTRUX_TOAST_BTN_TEXT,
                  backgroundColor: EVOTRUX_TOAST_HOVER_BG,
                },
              }}
            >
              Refresh
            </Button>
          </Tooltip>
          <Tooltip title="Dismiss">
            <IconButton
              aria-label="Dismiss notification"
              onClick={onDismiss}
              size="small"
              sx={{
                color: EVOTRUX_TOAST_ICON,
                flexShrink: 0,
                ml: -0.5,
                p: 0.5,
                "&:hover": { backgroundColor: EVOTRUX_TOAST_HOVER_BG },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
    </Snackbar>
  );
}

interface ShipmentLiveUpdateControllerProps {
  shipmentId: string | undefined;
  role: Role | null;
}

function ShipmentLiveUpdateControllerInner(props: UseShipmentLiveUpdatesProps) {
  const { shipmentId, role } = props;
  const { pendingUpdateType, onAccept, onDismiss } = useShipmentLiveUpdates({
    shipmentId,
    role,
  });
  return (
    <ShipmentLiveUpdateBanner
      pendingUpdateType={pendingUpdateType}
      onAccept={onAccept}
      onDismiss={onDismiss}
    />
  );
}

export function ShipmentLiveUpdateController(
  props: ShipmentLiveUpdateControllerProps,
) {
  const { shipmentId: rawShipmentId, role } = props;
  const parsedShipmentId = Number(rawShipmentId);

  if (role === null || !(parsedShipmentId > 0)) {
    return null;
  }

  return (
    <ShipmentLiveUpdateControllerInner
      shipmentId={parsedShipmentId}
      role={role}
    />
  );
}
